import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchPlans } from "../services/plansService";
import type { SubscriptionPlan } from "../types/billing.types";

export type PlansStatus = "loading" | "ready" | "failed";

export interface PlansState {
  /** What is on sale, cheapest commitment first. */
  plans: SubscriptionPlan[];
  status: PlansStatus;
  error?: string;
  reload: () => void;
}

const EMPTY: SubscriptionPlan[] = [];

export const PLANS_QUERY_KEY = ["billing", "plans"] as const;

/**
 * What Naurist sells, off the backend's own price list.
 *
 * Every screen that quotes a figure reads it through here — the cards, the
 * checkout's summary, the receipt — so what is advertised, what is charged and
 * what is receipted are one answer fetched once rather than three copies of a
 * number in a bundle.
 *
 * Unlike the other hooks in this feature there is no `SUBSCRIPTIONS_LIVE` guard:
 * that switch is about endpoints that do not exist yet, and this one does. A
 * failure is a real failure and is reported as one — the screens draw the way
 * back rather than falling back to a price nobody has confirmed, because a
 * stale figure on a card is worse than an honest "we could not load this".
 *
 * Backed by TanStack Query, same as the other resources in this app: a screen
 * visited again shows the last price list immediately while a fresh read
 * checks it behind the scenes. The list rarely changes and a checkout still
 * charges off the plan id the server holds, not off what this cache is
 * showing — so the brief window before a genuine price change lands is a
 * display detail, not a billing risk.
 *
 * Must be used inside a Clerk provider.
 */
export function usePlans(): PlansState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: PLANS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPlans(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) console.error("plans failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    plans: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}

export default usePlans;
